/**
 * Client risk actions — the screen four shipped controls never had.
 *
 * The security hold (0079) and the destination exceptions to the 180-day
 * return-to-source rule (0083) both had a working API and no way for staff to
 * use them, so every one of these actions needed a developer with database
 * access — the opposite of the segregation of duties they exist to enforce.
 *
 * Both are about one client, so they share a page and a client picker. The
 * blocked-country list is global and super_admin-gated, so it lives on its own
 * screen; the source-account picker belongs on the attribution page, next to
 * the wire it explains.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge, { type BadgeTone } from '../../components/StatusBadge';
import agentService from '../../services/agent.service';
import authService from '../../services/auth.service';
import bankAccountsService, { type ClientBankAccount } from '../../services/bankAccounts.service';
import clientRiskService, {
  type ClientRiskHistory,
  type DestinationException,
  type DestinationExceptionStatus,
  type DestinationExceptions,
  type SecurityHoldStatus,
} from '../../services/clientRisk.service';
import managerService from '../../services/manager.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime } from '../../lib/datetime';
import { colors } from '../../styles/theme';
import {
  HOLD_FREEZES,
  canDecide,
  describeExceptionStatus,
  summariseHold,
  whyCannotDecide,
} from './clientRisk.helpers';

interface ClientOption {
  id: string;
  label: string;
}

const inputStyle = { border: `1px solid ${colors.border}` };

const EXCEPTION_TONE: Record<DestinationExceptionStatus, BadgeTone> = {
  proposed: 'warning',
  approved: 'success',
  rejected: 'danger',
  expired: 'neutral',
};

function Card({ title, subtitle, children }: Readonly<{
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}>): JSX.Element {
  return (
    <section
      className="rounded-2xl p-5 mb-6"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>{title}</h2>
      {subtitle && (
        <p className="text-sm mb-4" style={{ color: colors.muted }}>{subtitle}</p>
      )}
      {children}
    </section>
  );
}

function StepUpField({ id, value, onChange, disabled }: Readonly<{
  id: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}>): JSX.Element {
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
        Authentication code
      </label>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 8))}
        placeholder="6-digit code"
        disabled={disabled}
        className="w-full px-3 py-2 rounded-lg text-sm font-mono tracking-widest"
        style={inputStyle}
      />
    </div>
  );
}

export default function ClientRisk(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const clientId = params.get('clientId') ?? '';
  const role = authService.getRole();
  const [viewerId, setViewerId] = useState('');
  // The id decides whether the decide buttons are offered at all: the proposer
  // of an exception may never be its decider.
  useEffect(() => {
    authService
      .getCurrentUser()
      .then((me) => setViewerId(me.id))
      .catch(() => setViewerId(''));
  }, []);
  const viewer = useMemo(() => ({ id: viewerId, role }), [viewerId, role]);

  const [clients, setClients] = useState<ClientOption[]>([]);
  const [hold, setHold] = useState<SecurityHoldStatus | null>(null);
  const [exceptions, setExceptions] = useState<DestinationExceptions | null>(null);
  const [accounts, setAccounts] = useState<ClientBankAccount[]>([]);
  const [risk, setRisk] = useState<ClientRiskHistory | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const rows = role === 'agent'
          ? await agentService.listMyClients()
          : await managerService.listClients();
        setClients(
          rows
            .map((c) => ({
              id: c.id,
              label: `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() || c.email,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        );
      } catch (e: unknown) {
        setError(getApiErrorMessage(e, 'Could not load the client list.'));
      }
    })();
  }, [role]);

  const reload = useCallback(async () => {
    if (!clientId) {
      setHold(null);
      setExceptions(null);
      setAccounts([]);
      setRisk(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [h, x, a, r] = await Promise.all([
        clientRiskService.securityHold(clientId),
        clientRiskService.destinationExceptions(clientId),
        bankAccountsService.listForClient(clientId),
        clientRiskService.riskHistory(clientId),
      ]);
      setHold(h);
      setExceptions(x);
      setAccounts(a);
      setRisk(r);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load this client’s risk actions.'));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { void reload(); }, [reload]);

  const summary = summariseHold(hold);

  return (
    <div className="max-w-5xl">
      <PageHeader
        title="Client risk actions"
        subtitle="What is frozen for this client, and what may be unfrozen."
      />

      <Card title="Client">
        <label htmlFor="risk-client" className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
          Whose account are you looking at?
        </label>
        <select
          id="risk-client"
          value={clientId}
          onChange={(e) => setParams(e.target.value ? { clientId: e.target.value } : {})}
          className="w-full px-3 py-2 rounded-lg text-sm"
          style={inputStyle}
        >
          <option value="">— select client —</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>
      </Card>

      {error && (
        <div className="mb-4 p-3 rounded-lg text-sm"
             style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}>
          {error}
        </div>
      )}
      {flash && (
        <div className="mb-4 p-3 rounded-lg text-sm"
             style={{ background: colors.successBg, border: `1px solid ${colors.successBorder}`, color: colors.success }}>
          {flash}
        </div>
      )}

      {clientId && loading && (
        <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>
      )}

      {clientId && !loading && risk && <RiskHistoryPanel risk={risk} />}

      {clientId && !loading && summary && (
        <SecurityHoldPanel
          clientId={clientId}
          summary={summary}
          canLift={role === 'manager' || role === 'super_admin'}
          onDone={(msg) => { setFlash(msg); void reload(); }}
          onError={setError}
        />
      )}

      {clientId && !loading && exceptions && (
        <ExceptionsPanel
          clientId={clientId}
          data={exceptions}
          accounts={accounts}
          viewer={viewer}
          onDone={(msg) => { setFlash(msg); void reload(); }}
          onError={setError}
        />
      )}
    </div>
  );
}

/**
 * Why this client sits in the band they sit in.
 *
 * The band alone is what a screen used to show. The history is the answer to
 * the question an examiner actually asks — which factors, scored how, and what
 * triggered the reassessment — and it is append-only, so it is also the record
 * that nobody quietly re-graded a client.
 */
/** One rule for how a band reads, so the badge and the history cannot disagree. */
const BAND_TONE: Record<string, BadgeTone> = { high: 'danger', medium: 'warning', low: 'success' };

/**
 * A risk factor as text. Objects and arrays are serialised deliberately: letting
 * one reach String() prints "[object Object]", which tells a reviewer nothing
 * and looks like a bug in the screen rather than in the data.
 */
function renderFactor(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value) ?? '—';
}

function bandTone(band: string | null): BadgeTone {
  // An unknown band is not 'low'. Defaulting an unrecognised grade to the
  // reassuring colour is the one mistake this function must not make.
  return band === null ? 'neutral' : BAND_TONE[band] ?? 'neutral';
}

function RiskHistoryPanel({ risk }: Readonly<{ risk: ClientRiskHistory }>): JSX.Element {
  const [open, setOpen] = useState(false);
  const band = risk.current?.risk_band ?? null;
  const tone: BadgeTone = bandTone(band);

  return (
    <Card
      title="Risk band"
      subtitle={
        risk.current
          ? 'The band decides what this client must provide and what they may do.'
          : 'This client has never been classified.'
      }
    >
      {risk.current && (
        <>
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <StatusBadge tone={tone}>{risk.current.risk_band}</StatusBadge>
            <span className="text-sm" style={{ color: colors.muted }}>
              score {risk.current.risk_score} · {risk.current.trigger.replace(/_/g, ' ')} ·{' '}
              {formatDateTime(risk.current.created_at)}
            </span>
          </div>
          {risk.current.notes && (
            <p className="text-sm mb-3" style={{ color: colors.inkSoft }}>{risk.current.notes}</p>
          )}
          <Factors factors={risk.current.factors} />
        </>
      )}

      {risk.history.length > 1 && (
        <>
          <button type="button" onClick={() => setOpen((v) => !v)}
                  className="text-sm font-semibold mt-4" style={{ color: colors.navy }}>
            {open ? 'Hide history' : `Show all ${risk.history.length} classifications`}
          </button>
          {open && (
            <ul className="space-y-3 mt-3">
              {risk.history.map((h) => (
                <li key={h.id} className="rounded-xl p-3" style={{ border: `1px solid ${colors.border}` }}>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <StatusBadge tone={bandTone(h.risk_band)}>
                      {h.risk_band}
                    </StatusBadge>
                    <span className="text-xs" style={{ color: colors.muted }}>
                      score {h.risk_score} · {h.trigger.replace(/_/g, ' ')} · {formatDateTime(h.created_at)}
                    </span>
                  </div>
                  {h.notes && <p className="text-xs" style={{ color: colors.inkSoft }}>{h.notes}</p>}
                  <Factors factors={h.factors} />
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Card>
  );
}

/** The inputs a band was computed from, rendered as they were recorded. */
function Factors({ factors }: Readonly<{ factors: Record<string, unknown> }>): JSX.Element | null {
  const entries = Object.entries(factors ?? {});
  if (entries.length === 0) return null;
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 mt-2 text-xs">
      {entries.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-3">
          <dt style={{ color: colors.muted }}>{k.replace(/_/g, ' ')}</dt>
          <dd style={{ color: colors.ink }}>
            {renderFactor(v)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function SecurityHoldPanel({ clientId, summary, canLift, onDone, onError }: Readonly<{
  clientId: string;
  summary: NonNullable<ReturnType<typeof summariseHold>>;
  canLift: boolean;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}>): JSX.Element {
  const [note, setNote] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const ready = note.trim().length >= 10 && code.trim().length >= 6 && !busy;

  const lift = async (): Promise<void> => {
    setBusy(true);
    try {
      await clientRiskService.liftSecurityHold(clientId, note.trim(), code.trim());
      setNote('');
      setCode('');
      onDone('The hold is lifted. This client can move money again.');
    } catch (e: unknown) {
      onError(getApiErrorMessage(e, 'Could not lift the hold.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Security hold" subtitle={summary.detail}>
      <div className="flex items-center gap-3 mb-4">
        <StatusBadge tone={summary.onHold ? 'danger' : 'success'}>{summary.headline}</StatusBadge>
      </div>

      {summary.onHold && (
        <>
          <p className="text-sm mb-2" style={{ color: colors.inkSoft }}>While the hold is on, this client cannot:</p>
          <ul className="text-sm mb-4 pl-5 list-disc" style={{ color: colors.muted }}>
            {HOLD_FREEZES.map((f) => <li key={f}>{f}</li>)}
          </ul>

          {canLift ? (
            <div className="rounded-xl p-4" style={{ border: `1px solid ${colors.border}` }}>
              <p className="text-sm mb-3" style={{ color: colors.inkSoft }}>
                Lift it only after verifying the account holder yourself, away from the portal —
                a call to the number on file, or in person. What you write here is the record of
                that check.
              </p>
              <label htmlFor="hold-note" className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
                How did you verify them?
              </label>
              <textarea
                id="hold-note"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 500))}
                placeholder="Called +52 ·· ··· 4821 on file, confirmed the last four digits of the account and the date it was added."
                disabled={busy}
                className="w-full px-3 py-2 rounded-lg text-sm mb-3"
                style={inputStyle}
              />
              <div className="mb-3">
                <StepUpField id="hold-mfa" value={code} onChange={setCode} disabled={busy} />
              </div>
              <button
                type="button"
                disabled={!ready}
                onClick={() => { void lift(); }}
                className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                style={{ background: colors.navy, color: '#FFFFFF' }}
              >
                {busy ? 'Lifting…' : 'Lift the hold'}
              </button>
            </div>
          ) : (
            <p className="text-sm" style={{ color: colors.muted }}>A manager lifts a hold.</p>
          )}
        </>
      )}

      {summary.events.length > 0 && (
        <table className="w-full text-sm mt-4">
          <thead>
            <tr style={{ color: colors.muted }}>
              <th className="text-left font-medium py-1">When</th>
              <th className="text-left font-medium py-1">What</th>
              <th className="text-left font-medium py-1">Note</th>
            </tr>
          </thead>
          <tbody>
            {summary.events.map((e) => (
              <tr key={`${e.created_at}-${e.action}`} style={{ borderTop: `1px solid ${colors.border}` }}>
                <td className="py-2 whitespace-nowrap" style={{ color: colors.muted }}>{formatDateTime(e.created_at)}</td>
                <td className="py-2" style={{ color: colors.ink }}>{e.action}</td>
                <td className="py-2" style={{ color: colors.muted }}>{e.note ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function ExceptionsPanel({ clientId, data, accounts, viewer, onDone, onError }: Readonly<{
  clientId: string;
  data: DestinationExceptions;
  accounts: ClientBankAccount[];
  viewer: { id: string; role: string | null };
  onDone: (message: string) => void;
  onError: (message: string) => void;
}>): JSX.Element {
  const [accountId, setAccountId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const readyToPropose = accountId !== '' && reason.trim().length >= 20 && !busy;

  const propose = async (): Promise<void> => {
    setBusy(true);
    try {
      await clientRiskService.proposeException(clientId, accountId, reason.trim());
      setAccountId('');
      setReason('');
      onDone('Proposed. A different manager decides it.');
    } catch (e: unknown) {
      onError(getApiErrorMessage(e, 'Could not propose the exception.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="Where their money may go"
      subtitle={
        data.window_ends_on
          ? `For 180 days after they last funded, money goes back only to an account that funded. That ends on ${data.window_ends_on}.`
          : 'This client has no funding account on record yet, so the 180-day rule does not bind them.'
      }
    >
      <div className="rounded-xl p-4 mb-5" style={{ border: `1px solid ${colors.border}` }}>
        <p className="text-sm mb-3" style={{ color: colors.inkSoft }}>
          Propose an exception when the funding account genuinely cannot receive the money —
          it was closed, or the bank rejected the transfer. Say what you saw; someone else decides.
        </p>
        <label htmlFor="exc-account" className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
          Account to allow
        </label>
        <select
          id="exc-account"
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
          disabled={busy}
          className="w-full px-3 py-2 rounded-lg text-sm mb-3"
          style={inputStyle}
        >
          <option value="">— select account —</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.account_type} ····{a.account_number_last4} · {a.holder_name}
            </option>
          ))}
        </select>
        <label htmlFor="exc-reason" className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
          Why, and what you saw
        </label>
        <textarea
          id="exc-reason"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, 1000))}
          placeholder="The funding account was closed on 3 September; the bank's closure letter is attached to the case."
          disabled={busy}
          className="w-full px-3 py-2 rounded-lg text-sm mb-3"
          style={inputStyle}
        />
        <button
          type="button"
          disabled={!readyToPropose}
          onClick={() => { void propose(); }}
          className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
          style={{ background: colors.navy, color: '#FFFFFF' }}
        >
          {busy ? 'Proposing…' : 'Propose an exception'}
        </button>
      </div>

      {data.exceptions.length === 0 ? (
        <p className="text-sm" style={{ color: colors.muted }}>No exceptions have been proposed for this client.</p>
      ) : (
        <ul className="space-y-3">
          {data.exceptions.map((x) => (
            <ExceptionRow
              key={x.id}
              exception={x}
              viewer={viewer}
              onDone={onDone}
              onError={onError}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

function ExceptionRow({ exception, viewer, onDone, onError }: Readonly<{
  exception: DestinationException;
  viewer: { id: string; role: string | null };
  onDone: (message: string) => void;
  onError: (message: string) => void;
}>): JSX.Element {
  const [note, setNote] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const mayDecide = canDecide(exception, viewer);
  const blockedWhy = whyCannotDecide(exception, viewer);
  const ready = note.trim().length >= 10 && code.trim().length >= 6 && !busy;

  const decide = async (approve: boolean): Promise<void> => {
    setBusy(true);
    try {
      await clientRiskService.decideException(exception.id, approve, note.trim(), code.trim());
      onDone(approve ? 'Exception approved.' : 'Exception rejected.');
    } catch (e: unknown) {
      onError(getApiErrorMessage(e, 'Could not record the decision.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="rounded-xl p-4" style={{ border: `1px solid ${colors.border}` }}>
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <StatusBadge tone={EXCEPTION_TONE[exception.status]}>
          {describeExceptionStatus(exception.status)}
        </StatusBadge>
        <span className="text-xs" style={{ color: colors.muted }}>
          Proposed {formatDateTime(exception.proposed_at)}
        </span>
      </div>
      <p className="text-sm mb-2" style={{ color: colors.ink }}>{exception.reason}</p>
      {exception.decision_note && (
        <p className="text-sm mb-2" style={{ color: colors.muted }}>
          Decision: {exception.decision_note}
        </p>
      )}

      {blockedWhy && (
        <p className="text-xs" style={{ color: colors.muted }}>{blockedWhy}</p>
      )}

      {mayDecide && (
        <div className="mt-3">
          <label htmlFor={`exc-note-${exception.id}`} className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
            Your decision, and why
          </label>
          <textarea
            id={`exc-note-${exception.id}`}
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 1000))}
            disabled={busy}
            className="w-full px-3 py-2 rounded-lg text-sm mb-3"
            style={inputStyle}
          />
          <div className="mb-3">
            <StepUpField id={`exc-mfa-${exception.id}`} value={code} onChange={setCode} disabled={busy} />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!ready}
              onClick={() => { void decide(true); }}
              className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
              style={{ background: colors.navy, color: '#FFFFFF' }}
            >
              Approve
            </button>
            <button
              type="button"
              disabled={!ready}
              onClick={() => { void decide(false); }}
              className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
              style={{ border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}
            >
              Reject
            </button>
          </div>
        </div>
      )}
    </li>
  );
}
